'use client';

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';

interface CarCardProps {
  image: string;
  type: string;
  location: string;
  price: number;
  roundedPrice: number;
  currency: string;
  year: number;
  mileage: number;
  engine: string;
  transmission: {
    speed: string;
    t_type: string;
  };
  body_type: string;
  variant: string;
  horse_power: string;
  color: string;
  listingUrl?: string;
  config_hash_exact?: string;
  config_hash_similar?: string;
  listingId?: string;
  build?: string;
  // Raw Elastic result object — passed to vehicle detail via router state
  // so the detail page never needs to re-fetch or touch Elastic state
  rawResult?: Record<string, any>;
}

const normalizeValue = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null) return '\u2014';
  if (typeof value === 'string') {
    const str = value.trim();
    if (str === '' || str.toLowerCase() === 'other') return '\u2014';
    return str;
  }
  return value > 0 ? value.toString() : '\u2014';
};

const formatPrice = (price: number, currency: string): string => {
  if (price <= 0) return '\u2014';
  const symbol =
    currency === 'EUR' ? 'EUR' : currency === 'USD' ? 'USD' : currency === 'GBP' ? 'GBP' : currency;
  return `${symbol} ${price.toLocaleString('de-DE')}`;
};

const getTrafficSource = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  if (utmSource === 'google' && utmMedium === 'cpc') return 'google_ads';
  if (utmSource === 'google_ads') return 'google_ads';
  return utmSource || 'native';
};

const CarCard: React.FC<CarCardProps> = ({
  image,
  type,
  location,
  price,
  roundedPrice,
  currency,
  year,
  mileage,
  engine,
  transmission,
  body_type,
  variant,
  horse_power,
  color,
  listingUrl,
  config_hash_exact,
  config_hash_similar,
  listingId,
  build,
  rawResult,
}) => {
  const navigate = useNavigate();

  // Guard: fire impression event only once per mount
  const hasTrackedImpression = React.useRef(false);

  // Guard: prevent double-navigation on fast double-click
  const isNavigating = React.useRef(false);

  React.useEffect(() => {
    if (hasTrackedImpression.current) return;
    hasTrackedImpression.current = true;

    const utm_source = getTrafficSource();
    try {
      posthog.capture('view_listings', {
        utm_source,
        listing_url: listingUrl,
        car_type: type,
        location,
        year,
        price,
        mileage,
        timestamp: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('PostHog impression capture failed', err);
    }
  }, [listingUrl, type, location, year, price, mileage]);

  // Reset navigation guard whenever the card identity changes
  // (e.g. after returning to results and the list re-renders with fresh props)
  React.useEffect(() => {
    isNavigating.current = false;
  }, [listingId, listingUrl]);

  const handleCardClick = (e: React.MouseEvent) => {
    // Let native anchor tags handle themselves
    if ((e.target as HTMLElement).closest('a')) return;

    // Prevent double-fire on fast double-click or event bubbling
    if (isNavigating.current) return;
    isNavigating.current = true;

    const utm_source = getTrafficSource();
    try {
      if (typeof posthog !== 'undefined' && posthog.capture) {
        posthog.capture('view_listing_click', {
          utm_source,
          listing_id: listingId,
          listing_url: listingUrl,
          car_type: type,
          year,
          price,
        });
      }
    } catch (err) {
      console.warn('PostHog capture failed', err);
    }

    if (listingId) {
      // KEY FIX: pass the full raw Elastic result via router state.
      // VehicleDetail reads this directly — no Elastic query, no setSearchTerm,
      // no URL state pollution. Back-navigation restores the search page cleanly.
      navigate(`/vehicle/${listingId}`, {
        state: { vehicle: rawResult ?? null },
      });
    } else if (listingUrl) {
      window.open(listingUrl, '_blank', 'noopener,noreferrer');
      // Reset immediately for external links — we stay on the same page
      isNavigating.current = false;
    }
  };

  const hasValidImage = image && !image.includes('porsche-placeholder');

  const genericBuilds = ['other', 'standard', 'original', 'factory'];
  const buildLabel =
    build && build !== '\u2014' && !genericBuilds.includes(build.toLowerCase()) ? build : null;

  const displayType = normalizeValue(type);

  return (
    <article
      className="bg-mp-surface border border-mp-border hover:border-mp-accent transition-colors duration-200 cursor-pointer group flex flex-col md:flex-row h-auto md:h-[240px]"
      onClick={handleCardClick}
    >
      {/* Image */}
      <div className="w-full md:flex-1 h-48 md:h-full relative overflow-hidden bg-black">
        {hasValidImage ? (
          <img
            alt={displayType}
            className="object-cover w-full h-full opacity-80 group-hover:opacity-100 transition-all duration-[500ms] grayscale group-hover:grayscale-0"
            src={image}
          />
        ) : (
          <img
            alt={displayType}
            className="object-contain w-full h-full opacity-60 p-4 transition-all duration-[500ms] grayscale group-hover:grayscale-0 group-hover:opacity-100"
            src={image}
          />
        )}
      </div>

      {/* Data */}
      <div className="w-full md:w-1/2 md:max-w-[340px] p-4 flex flex-col justify-between">
        <div className="mb-2 min-h-0">
          <div className="flex items-start justify-between gap-2">
            <h2 className="font-display text-sm font-semibold text-mp-text uppercase tracking-wide leading-tight group-hover:text-primary transition-colors line-clamp-2">
              {displayType}
            </h2>
            {buildLabel && (
              <span className="font-mono text-[9px] leading-none text-mp-accent border border-mp-accent bg-mp-accent/10 px-1.5 py-[2px] uppercase tracking-wider whitespace-nowrap shrink-0 mt-0.5">
                {buildLabel}
              </span>
            )}
          </div>
          <p className="font-mono text-xs text-mp-muted mt-0.5">
            {formatPrice(roundedPrice, currency)}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-auto border-t border-mp-border pt-2">
          <DataCell label="Year" value={normalizeValue(year)} />
          <DataCell label="Transmission" value={normalizeValue(transmission.t_type)} truncate />
          <DataCell
            label="Mileage"
            value={mileage > 0 ? `${mileage.toLocaleString('de-DE')} KM` : '\u2014'}
          />
          <DataCell
            label="Output"
            value={
              horse_power !== '\u2014' && horse_power !== '' ? `${horse_power} PS` : '\u2014'
            }
          />
          <DataCell label="Color" value={normalizeValue(color)} truncate />
          <DataCell label="Location" value={normalizeValue(location)} truncate />
        </div>
      </div>
    </article>
  );
};

const DataCell: React.FC<{ label: string; value: string; truncate?: boolean }> = ({
  label,
  value,
  truncate,
}) => (
  <div>
    <p className="font-display text-[10px] text-mp-muted uppercase tracking-widest mb-0.5">
      {label}
    </p>
    <p className={`font-mono text-[13px] text-mp-text ${truncate ? 'truncate' : ''}`}>{value}</p>
  </div>
);

export default CarCard;
